import "./App.less";
import React, {useCallback, useEffect, useRef, useState} from "react";
import AppHeader from "./components/AppHeader";
import AppAside from "./components/AppAside";
import { Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Brand from "./components/pages/Brand";
import LinkPages from "./components/pages/LinkPages";
import Designs from "./components/pages/Designs";
import OnlineMenus from "./components/pages/OnlineMenus";
import { env } from "./env/env";
import DashboardApi from "./api/DashboardApi";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  cloverDeltaUpdatesState,
  cloverRestaurantsState,
  designUsingCloverState,
  designUsingSquareState,
  designUsingToastState, designUsingTreezState,
  designUsingPopmenuState,
  locationsState,
  memberState,
  mobileState,
  posState,
  squareDeltaUpdatesState,
  squareRestaurantsState,
  toastDeltaUpdatesState,
  toastRestaurantsState,
  treezDeltaUpdatesState, treezRestaurantsState,
  popmenuDeltaUpdatesState, popmenuRestaurantsState, paymentJsLoadedState
} from "./state/atoms";
import Logo from "./components/Logo";
import RouterService from "./service/RouterService";
import { LocationDto, MemberDto, MemberDtoPlanEnum } from "./generated/dashboard-client";
import { AppContext } from "./AppContext";
import AccountDashboard from "./components/account/AccountDashboard";
import { UsersPage } from "./components/account/page/UsersPage";
import { SubscriptionPage } from "./components/account/page/SubscriptionPage";
import { BillingPage } from "./components/account/page/BillingPage";
import { EditLocationPage } from "./components/account/page/locations/EditLocationPage";
import { LocationsPage } from "./components/account/page/locations/LocationsPage";
import { ManageBrandPage } from "./components/account/page/ManageBrandPage";
import { BrandsPage } from "./components/account/page/BrandsPage";
import { TeamPage } from "./components/account/page/team/TeamPage";
import { PrintHistoryPage } from "./components/account/page/PrintHistoryPage";
import CookieNotification from "./components/CookieNotification";
import { PrintCheckoutPage } from "./components/print/PrintCheckoutPage";
import Storage from "./util/Storage";
import { isStageEnv, isTestEnv } from "./util/constants";
import { isContentPath, isDSAPath, isLandingPath, isPrintPath } from "./util/utils";
import RegisterPage from "./components/register/RegisterPage";
import { logout } from "./service/AuthService";
import { PrintHistoryPageNew } from "./components/account/page/PrintHistoryPageNew";
import { PrintOrderDetailPage } from "./components/account/page/PrintOrderDetailPage";
import PasswordPage from "./components/PasswordPage";
import PasswordResetPage from "./components/PasswordResetPage";
import { getJSPath, loadJS } from "./util/ScriptLoader";
import ContentPage from "./components/content/ContentPage";
import BentoContent from "./components/content/BentoContent";
import AffiliateContent from "./components/content/AffiliateContent";
import AffiliateContentForm from "./components/content/AffiliateContentForm";
import AccountsApi from "./api/AccountsApi";
import ResourceShowcasePage from "./components/content/ResourceShowcasePage";
import DigitalAssetsPage from "./components/content/DigitalAssetsPage";
import AccessRemoved from "./components/pages/AccessRemoved";
import { updateMemberStatus } from "./service/AnalyticsService";
import DesignUpdater from "./components/pages/DesignUpdater";
import PosApi from "./api/PosApi";
import {
  getAllCloverDeltaUpdatesUnfiltered,
  getAllToastDeltaUpdatesUnfiltered,
  getCloverDeltaUpdates,
  getToastDeltaUpdates,
  resolveAllToastPendingChanges,
  resolveAllCloverPendingChanges,
  getAllSquareDeltaUpdatesUnfiltered,
  getSquareDeltaUpdates,
  resolveAllSquarePendingChanges,
  getAllTreezDeltaUpdatesUnfiltered, getTreezDeltaUpdates, resolveAllTreezPendingChanges,
  getAllPopmenuDeltaUpdatesUnfiltered, getPopmenuDeltaUpdates, resolveAllPopmenuPendingChanges
} from "./service/PosService";
import ToastPartnerConnect from "./components/content/ToastPartnerConnectContent";
import OAuthPage from "./components/OAuthPage";
import Square from "./components/pages/Square";
import { ChatService } from "./service/ChatService";
import PressPage from "./components/content/PressPage";
import ToastIntegrationAnnouncement from "./components/content/article/ToastIntegrationAnnouncement";
import { SiteBanner } from "./components/SiteBanner";
import ShamrockPage from "./components/content/ShamrockPage";
import ShamrockMenuServices from "./components/content/ShamrockMenuServices";
import ShamrockResourceCenter from "./components/content/ShamrockResourceCenter";
import PartnersPage from "./components/content/PartnersPage";
import PartnerPage from "./components/content/PartnerPage";
import DSAManager from "./components/pages/DSAManager";
import { datadogRum } from '@datadog/browser-rum';
import MembersApi from "./api/MembersApi";
import UnpaidInvoiceModal from "./components/pages/UnpaidInvoiceModal";
import DisplayAppAnnouncement from "./components/content/article/DisplayAppAnnouncement";
import StreamOAuthPage from "./components/StreamOAuthPage";
import { MarketingBanner } from "./components/MarketingBanner";
import Popmenu from "./components/pages/Popmenu";
import TextBlockApi from "./api/TextBlockApi";
//import SurveyModal from "./components/design/SurveyModal";

const dbPath = env.dashboardPath;

function App() {
  const location = useLocation();
  let navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const [searchParams] = useSearchParams();

  const setMember = useSetRecoilState(memberState);
  const member = useRecoilValue(memberState);
  const [locations, setLocations] = useRecoilState(locationsState);
  const [pos, setPos] = useRecoilState(posState);
  const [isAppLoading, setAppLoading] = useState(true);
  const [dashboardLoaded, setDashboardLoaded] = useState(false);
  const [accessRemoved, setAccessRemoved] = useState(false);
  const [activeLocation, setActiveLocation] = useState({} as LocationDto);
  const [activeLocationId, setActiveLocationId] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const [paymentJsLoaded, setPaymentJsLoaded] = useRecoilState(paymentJsLoadedState);
  const [isSingleUserAcc, setIsSingleUserAcc] = useState(false);
  const setMobile = useSetRecoilState(mobileState);

  const [toastRestaurants, setToastRestaurants] = useRecoilState(toastRestaurantsState);
  const setToastDeltaUpdates = useSetRecoilState(toastDeltaUpdatesState);
  const [designsUsingToast, setDesignsUsingToast] = useRecoilState(designUsingToastState);
  const [loadingToastChanges, setLoadingToastChanges] = useState(false);

  const [cloverRestaurants, setCloverRestaurants] = useRecoilState(cloverRestaurantsState);
  const setCloverDeltaUpdates = useSetRecoilState(cloverDeltaUpdatesState);
  const [designsUsingClover, setDesignsUsingClover] = useRecoilState(designUsingCloverState);
  const [loadingCloverChanges, setLoadingCloverChanges] = useState(false);

  const [squareRestaurants, setSquareRestaurants] = useRecoilState(squareRestaurantsState);
  const setSquareDeltaUpdates = useSetRecoilState(squareDeltaUpdatesState);
  const [designsUsingSquare, setDesignsUsingSquare] = useRecoilState(designUsingSquareState);
  const [loadingSquareChanges, setLoadingSquareChanges] = useState(false);

  const [treezRestaurants, setTreezRestaurants] = useRecoilState(treezRestaurantsState);
  const setTreezDeltaUpdates = useSetRecoilState(treezDeltaUpdatesState);
  const [designsUsingTreez, setDesignsUsingTreez] = useRecoilState(designUsingTreezState);
  const [loadingTreezChanges, setLoadingTreezChanges] = useState(false);

  const [popmenuRestaurants, setPopmenuRestaurants] = useRecoilState(popmenuRestaurantsState);
  const setPopmenuDeltaUpdates = useSetRecoilState(popmenuDeltaUpdatesState);
  const [designsUsingPopmenu, setDesignsUsingPopmenu] = useRecoilState(designUsingPopmenuState);
  const [loadingPopmenuChanges, setLoadingPopmenuChanges] = useState(false);

  const [isUnverifiedToastUser, setIsUnverifiedToastUser] = useState(false);
  const [unpaidInvoiceUrl, setUnpaidInvoiceUrl] = useState('');
  //const [isSurveyModal, setSurveyModal] = useState(false);

  const [isMarketingBannerPage, setIsMarketingBannerPage] = useState(true);
  const [isSiteBanner, setIsSiteBanner] = useState(false);

  useEffect(() => {
    loadJS(getJSPath(), () => {
      setPaymentJsLoaded(true);
    });

    // @ts-ignore
    window['AccountsApi'] = AccountsApi;
  }, [setPaymentJsLoaded]);

  useEffect(() => {
    if (!member || !member.internalId || member.status !== 1) return;

    MembersApi.getUnpaidInvoicePaymentPageUrl().then(resp => {
      setUnpaidInvoiceUrl(resp.data.invoice || '');
    });
  }, [member]);

  useEffect(() => {
    if (!pos.enabled || member.clover) return;
    if (pos.toastEmail) {
      setLoadingToastChanges(true);
      PosApi.getToastRestaurants(pos.toastEmail).then(r => {
        setToastRestaurants(r.data);
        if (!r.data.length) {
          setLoadingToastChanges(false);
        }
      });
    } else {
      if (member.email) {
        PosApi.getToastRestaurants(member.email).then(r => {
          // Has MHM tile in toast but has not verified via Timber
          if (r.data.length > 0) {
            setIsUnverifiedToastUser(true);
          }
        })
      }
    }
  }, [setToastRestaurants, pos.enabled, pos.toastEmail, member.clover, member.email]);

  useEffect(() => {
    if (!pos.enabled || !member.clover) return;
    setLoadingCloverChanges(true);
    PosApi.getCloverRestaurants().then(r => {
      setCloverRestaurants(r.data);
      if (!r.data.length) {
        setLoadingCloverChanges(false);
      }
    });
  }, [setLoadingCloverChanges, setCloverRestaurants, pos.enabled, member.clover]);

  useEffect(() => {
    if (!pos.enabled || !pos.isSquare) return;
    setLoadingSquareChanges(true);
    PosApi.getSquareRestaurants().then(r => {
      setSquareRestaurants(r.data);
      if (!r.data.length) {
        setLoadingSquareChanges(false);
      }
    });
  }, [setLoadingSquareChanges, setSquareRestaurants, pos.enabled, pos.isSquare]);

  useEffect(() => {
    if (!pos.enabled || !pos.isTreez) return;
    setLoadingTreezChanges(true);
    PosApi.getTreezRestaurants().then(r => {
      setTreezRestaurants(r.data);
      if (!r.data.length) {
        setLoadingTreezChanges(false);
      }
    });
  }, [setLoadingTreezChanges, setTreezRestaurants, pos.enabled, pos.isTreez]);

  useEffect(() => {
    if (!pos.enabled || !pos.isPopmenu) return;
    setLoadingPopmenuChanges(true);
    PosApi.getPopmenuRestaurants().then(r => {
      setPopmenuRestaurants(r.data);
      if (!r.data.length) {
        setLoadingPopmenuChanges(false);
      }
    });
  }, [setLoadingPopmenuChanges, setPopmenuRestaurants, pos.enabled, pos.isPopmenu]);

  useEffect(() => {
    if (!toastRestaurants || !toastRestaurants.length || !designsUsingToast.length) return;

    getAllToastDeltaUpdatesUnfiltered(toastRestaurants).then(allUpdates => {
      getToastDeltaUpdates(allUpdates, toastRestaurants, designsUsingToast).then(updates => {
        setToastDeltaUpdates(updates);
        // setLoadingToastChanges(false);

        if (!Object.keys(updates).length) {
          resolveAllToastPendingChanges(allUpdates).then();
        }
      });
    });
  }, [toastRestaurants, designsUsingToast, setToastDeltaUpdates, location.pathname]);

  useEffect(() => {
    if (!cloverRestaurants || !cloverRestaurants.length || !designsUsingClover.length) return;

    getAllCloverDeltaUpdatesUnfiltered(cloverRestaurants).then(allUpdates => {
      getCloverDeltaUpdates(allUpdates, cloverRestaurants, designsUsingClover).then(updates => {
        setCloverDeltaUpdates(updates);
        setLoadingCloverChanges(false);

        if (!Object.keys(updates).length) {
          resolveAllCloverPendingChanges(allUpdates).then();
        }
      });
    });
  }, [cloverRestaurants, designsUsingClover, setCloverDeltaUpdates, location.pathname]);

  useEffect(() => {
    if (!squareRestaurants || !squareRestaurants.length || !designsUsingSquare.length) return;

    getAllSquareDeltaUpdatesUnfiltered(squareRestaurants).then(allUpdates => {
      getSquareDeltaUpdates(allUpdates, squareRestaurants, designsUsingSquare).then(updates => {
        setSquareDeltaUpdates(updates);
        setLoadingSquareChanges(false);

        if (!Object.keys(updates).length) {
          resolveAllSquarePendingChanges(allUpdates).then();
        }
      });
    });

  }, [squareRestaurants, designsUsingSquare, setSquareDeltaUpdates, location.pathname]);

  useEffect(() => {
    if (!treezRestaurants || !treezRestaurants.length || !designsUsingTreez.length) return;

    getAllTreezDeltaUpdatesUnfiltered(treezRestaurants).then(allUpdates => {
      getTreezDeltaUpdates(allUpdates, treezRestaurants, designsUsingTreez).then(updates => {
        setTreezDeltaUpdates(updates);
        setLoadingTreezChanges(false);

        if (!Object.keys(updates).length) {
          resolveAllTreezPendingChanges(allUpdates).then();
        }
      });
    });

  }, [treezRestaurants, designsUsingTreez, setTreezDeltaUpdates, location.pathname]);

  useEffect(() => {
    if (!popmenuRestaurants || !popmenuRestaurants.length || !designsUsingPopmenu.length) return;

    getAllPopmenuDeltaUpdatesUnfiltered(popmenuRestaurants).then(allUpdates => {
      getPopmenuDeltaUpdates(allUpdates, popmenuRestaurants, designsUsingPopmenu).then(updates => {
        setPopmenuDeltaUpdates(updates);
        setLoadingPopmenuChanges(false);

        if (!Object.keys(updates).length) {
          resolveAllPopmenuPendingChanges(allUpdates).then();
        }
      });
    });

  }, [popmenuRestaurants, designsUsingPopmenu, setPopmenuDeltaUpdates, location.pathname]);

  useEffect(() => {
    if (!activeLocationId) return;
    PosApi.getDesignsUsingToast(activeLocationId).then(resp => {
      setDesignsUsingToast(resp.data);
      setLoadingToastChanges(false);

      if (!resp.data || !resp.data.length) {
        setLoadingToastChanges(false);
      }
    });
  }, [activeLocationId, setDesignsUsingToast]);

  useEffect(() => {
    if (!activeLocationId || !pos.isClover) return;
    PosApi.getDesignsUsingClover(activeLocationId).then(resp => {
      setDesignsUsingClover(resp.data);
      if (!resp.data || !resp.data.length) {
        setLoadingCloverChanges(false);
      }
    });
  }, [activeLocationId, setDesignsUsingClover, pos.isClover]);

  useEffect(() => {
    if (!activeLocationId || !pos.isSquare) return;
    PosApi.getDesignsUsingSquare(activeLocationId).then(resp => {
      setDesignsUsingSquare(resp.data);
      if (!resp.data || !resp.data.length) {
        setLoadingSquareChanges(false);
      }
    });
  }, [activeLocationId, setDesignsUsingSquare, pos.isSquare]);

  useEffect(() => {
    if (!activeLocationId || !pos.isTreez) return;
    PosApi.getDesignsUsingTreez(activeLocationId).then(resp => {
      setDesignsUsingTreez(resp.data);
      if (!resp.data || !resp.data.length) {
        setLoadingTreezChanges(false);
      }
    });
  }, [activeLocationId, setDesignsUsingTreez, pos.isTreez]);

  useEffect(() => {
    if (!activeLocationId || !pos.isPopmenu) return;
    PosApi.getDesignsUsingPopmenu(activeLocationId).then(resp => {
      setDesignsUsingPopmenu(resp.data);
      if (!resp.data || !resp.data.length) {
        setLoadingPopmenuChanges(false);
      }
    });
  }, [activeLocationId, setDesignsUsingPopmenu, pos.isPopmenu]);

  useEffect(() => {
    if (locations.length === 0) return;
    const locationId = RouterService.getLocationFromPath(location.pathname);
    let found = locations[0];
    const storedLoc = window.localStorage.getItem("activeLocation");
    if (locationId) {
      found = locations.find((it) => it.internalId === locationId)!;
    } else if (storedLoc) {
      found = locations.find((it) => it.internalId!.toString() === storedLoc)!;
    }

    if (!found) {
      found = locations[0];
    }

    window.localStorage.setItem("activeLocation", `${found.internalId}`);
    setActiveLocation(found);
  }, [location, locations]);

  useEffect(() => {
    if (!activeLocation) return;
    setActiveLocationId(parseInt(activeLocation.internalId + ''));
  }, [activeLocation]);

  useEffect(() => {
    const { pathname } = location;
    if (isLandingPath(pathname) || isPrintPath(pathname) || isDSAPath(pathname)) {
      setIsMarketingBannerPage(false);
    } else {
      setIsMarketingBannerPage(true);
    }
  }, [location]);

  useEffect(() => {
    TextBlockApi.getByName("Dashboard Notice").then((resp) => {
      if (resp.data && resp.data.details) {
        setIsSiteBanner(true);
      }
    })
  }, []);

  const checkDashboardStats = useCallback(() => {
    if (!paymentJsLoaded || dashboardLoaded) return;
    setDashboardLoaded(true);
    DashboardApi.status().then((resp) => {
      const { member, locations, oldLoginKey, pos, toastEmail, clover, square, treez, popmenu, dsaBeta } = resp.data;
      const { id, internalId, logged, email, name, teamId, plan, ownerId, status, printInvoicing } = member!;

      setPos({
        enabled: !!pos,
        toastEmail: toastEmail ?? "",
        isClover: !!clover,
        isToast: !!toastEmail,
        isSquare: !!square,
        isTreez: !!treez,
        isPopmenu: !!popmenu
      });

      setTimeout(() => {
        ChatService.init(member!);
        initDataDog(member!);
        initPinterest(member!);
        initPagesense(member!);
      });

      if (plan === "REMOVED" && ownerId !== internalId) {
        setAccessRemoved(true);
        setAppLoading(false);
        return;
      }

      if (plan === "PAUSED" || plan === "DECLINED" || plan === "PAST_DUE" || plan === "CANCELLED") {
        const onDismiss = () => {
          logout().then(() => {
            window.location.href = env.basePath;
          });
        }

        if (ownerId !== internalId) {
          onDismiss();
        } else {
          if (plan === "PAUSED") window.MHM.ProModal.reactivateFromPaused({
            memberId: id!,
            onSuccess: () => window.location.reload(),
            onDismiss
          });
          if (plan === "DECLINED") window.MHM.ProModal.reactivateFromDeclined({
            memberId: id!,
            onSuccess: () => window.location.reload(),
            onDismiss
          });
          if (plan === "CANCELLED") window.MHM.ProModal.reactivateFromCancelled({
            memberId: id!,
            onSuccess: () => window.location.reload(),
            onDismiss
          });
          if (plan === "PAST_DUE") window.MHM.ProModal.payPastDue({
            memberId: id!,
            onSuccess: () => window.location.reload(),
            onDismiss
          });
        }
        return;
      }

      setMember({
        id: id ?? "",
        internalId: internalId ?? 0,
        teamId: teamId ?? "",
        email: email ?? "",
        name: name ?? "",
        logged: logged ?? false,
        owner: !ownerId || internalId === ownerId,
        plan: plan ?? MemberDtoPlanEnum.Free,
        oldLoginKey: oldLoginKey ?? "",
        status: status ?? 0,
        clover: member?.clover ?? false,
        ownerId: member?.ownerId ?? 0,
        dsaBeta: dsaBeta ?? false,
        printInvoicing: printInvoicing ?? false,
        downloads: member?.downloads ?? false,
        templates: member?.templates ?? false,
        printing: member?.printing ?? false
      });
      updateMemberStatus(internalId ?? 0, plan ?? "", plan ?? "", plan ?? "")

      if (member?.changePassword) {
        navigateRef.current("/account/password/reset");
        return;
      }

      AccountsApi.getUsersDetails(id!).then((resp) => {
        const users = resp.data.users || [];
        setIsSingleUserAcc((!ownerId || internalId === ownerId) && users.length === 0);
      })

      setAppLoading(false);

      let uniqueIds: string[] = [];
      let uniqueLocs: LocationDto[] = [];
      locations!.forEach((loc) => {
        if (!uniqueIds.includes(loc.id!)) {
          uniqueIds.push(loc.id!);
          uniqueLocs.push(loc);
        }
      });

      setLocations(uniqueLocs);

      /*let surveyShownIds = getCookie('mryumSurveyShown');
      if (!surveyShownIds.includes(internalId!.toString())) {
        setSurveyModal(true);
        setMrYumSurveyCookie(internalId!.toString());
      }*/

      const redirect = window.localStorage.getItem("redirect-path");
      if (redirect) {
        window.localStorage.removeItem("redirect-path");
        navigateRef.current(redirect);
      }
    }).catch((e) => {
      if (e.response.status === 401) {
        logout().then(() => {
          window.location.href = env.basePath;
        });
      }
      setAppLoading(false);
    });
  }, [setMember, setLocations, paymentJsLoaded, setPos, dashboardLoaded])

  function initDataDog(member: MemberDto) {
    datadogRum.init({
      applicationId: 'a9eb41c0-1c23-4de5-9302-c8a981bc5821',
      clientToken: 'pub8a2bc7e7c1fe41c60ada75d06bd3871d',
      site: 'datadoghq.com',
      service:'musthavemenus',
      env: isTestEnv ? 'test' : isStageEnv ? 'stage' : 'prod',
      sessionSampleRate:100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      silentMultipleInit: true,
    });
    datadogRum.setUser({
      id: member.internalId?.toString(),
      name: member.name!,
      email: member.email!,
      plan: member.plan!,
    })
  }

  function initPinterest(member: MemberDto) {
    (window as any).pintrk('load', '2613736158945', {
      em: member.email,
    });
    (window as any).pintrk('page');
    // console.log('Pinterest initialized');
  }

  function initPagesense(member: MemberDto) {
    window.pagesense = window.pagesense || [];
    if (member.email) {
      window.pagesense.push(['identifyUser', `${member.email}`]);
    }

    let created = new Date(member.createdDate!);
    let startedCounting = new Date('09/27/2023');
    if (created > startedCounting && member.numLogins === 3) {
      console.log('should show NPS poll');
      window.pagesense.push(['trackEvent', 'Third visit']);
    }
  }

  useEffect(() => {
    if (!searchParams.get('merchant_id') || !searchParams.get('code') || !searchParams.get('employee_id') || !searchParams.get('client_id') || member.clover) return;

    console.log('= redirecting aici', searchParams.get('code'), member.clover)

    navigate('/account/login' + window.location.search);
  }, [navigate, searchParams, member.clover]);

  useEffect(() => {
    if (isLandingPath(location.pathname)) {
      setAppLoading(false);
      return;
    }

    if (isContentPath(location.pathname)) {
      if (Storage.getToken()) {
        checkDashboardStats();
        return;
      } else {
        setAppLoading(false);
        return;
      }
    }

    if (Storage.getToken()) {
      checkDashboardStats();
      return;
    } else {
      window.localStorage.setItem("redirect-path", window.location.pathname);
      window.location.href = `/account/login`;
    }
  }, [checkDashboardStats, location]);

  useEffect(() => {
    DashboardApi.getIntercomData().then(async (resp) => {
      ChatService.addMemberDetails(resp.data);
    });
  }, []);

  const onLogin = useCallback(() => {
    setAppLoading(true);
  }, [setAppLoading]);

  useEffect(() => {
    if (window.outerWidth <= 991) {
      setMobile(true);
    }

    const handler = () => setMobile(window.innerWidth <= 991);

    window.addEventListener('resize', handler)
    return () => {
      window.removeEventListener('resize', handler);
    }
  }, [setMobile]);

  useEffect(() => {
    // console.log("isSiteBanner: " + isSiteBanner);
  }, [isSiteBanner]);

  return isAppLoading ? (
    <div className={"app-loading"}>
      <Logo />
    </div>
  ) : (
    <>
      {accessRemoved && <AccessRemoved />}
      {!!unpaidInvoiceUrl && <UnpaidInvoiceModal url={unpaidInvoiceUrl} />}
      {!accessRemoved && !unpaidInvoiceUrl && <AppContext.Provider
        value={{ location: activeLocation, setLocation: setActiveLocation }}
      >
        <div className="app">
          <CookieNotification />

          <MarketingBanner isVisiblePage={isMarketingBannerPage} />
          <SiteBanner setIsSiteBanner={setIsSiteBanner} />
          <AppHeader activeLocation={activeLocation} cartCount={cartCount} setCartCount={setCartCount} isMarketingBannerPage={isSiteBanner} />

          <main
            className={`${(isLandingPath(location.pathname) || isContentPath(location.pathname)) ? "full" : ""} ${!isSiteBanner ? 'no-promo-banner' : ''}`}
          >
            <AppAside
              activeLocation={activeLocation}
              isMarketingBannerPage={isSiteBanner}
            />

            <div className={`container ${isPrintPath(location.pathname) || isDSAPath(location.pathname) ? " full-width" : ""}`} >
              <Routes>
                <Route path={dbPath + "/designs/:locationId"} element={<Designs isSingleUserAcc={isSingleUserAcc} isUnverifiedToastUser={isUnverifiedToastUser} />} />
                <Route path={dbPath + "/designs"} element={<Designs isSingleUserAcc={isSingleUserAcc} isUnverifiedToastUser={isUnverifiedToastUser} />} />
                <Route path={dbPath + "/design-updater"} element={<DesignUpdater loading={loadingToastChanges || loadingCloverChanges || loadingSquareChanges || loadingTreezChanges || loadingPopmenuChanges} />} />
                <Route path={dbPath + "/dsa/*"} element={<DSAManager isMarketingBannerPage={isSiteBanner} />} />
                <Route path={dbPath + "/online-menus"} element={<OnlineMenus />} />
                <Route path={dbPath + "/online-menus/:locationId"} element={<OnlineMenus />} />
                <Route path={dbPath + "/link-pages/:locationId"} element={<LinkPages activeLocation={activeLocation} />} />
                <Route path={dbPath + "/link-pages"} element={<LinkPages activeLocation={activeLocation} />} />
                <Route path={dbPath + "/brand/:locationId"} element={<Brand activeLocation={activeLocation} />} />
                <Route path={dbPath + "/brand"} element={<Brand activeLocation={activeLocation} />} />
                <Route path={dbPath + "/print/checkout"} element={<PrintCheckoutPage path={"cart"} setCartCount={setCartCount} />} />
                <Route path={dbPath + "/print/checkout/cart"} element={<PrintCheckoutPage path={"cart"} setCartCount={setCartCount} />} />
                <Route path={dbPath + "/print/checkout/empty"} element={<PrintCheckoutPage path={"empty"} />} />
                <Route path={dbPath + "/print/checkout/cart"} element={<PrintCheckoutPage path={"cart"} />} />
                <Route path={dbPath + "/print/checkout/shipping"} element={<PrintCheckoutPage path={"shipping"} />} />
                <Route path={dbPath + "/print/checkout/payment"} element={<PrintCheckoutPage path={"payment"} setCartCount={setCartCount} />} />
                <Route path={dbPath + "/print/checkout/success"} element={<PrintCheckoutPage path={"success"} />} />
                <Route path={dbPath + "/square"} element={<Square />} />
                <Route path={dbPath + "/popmenu"} element={<Popmenu />} />

                <Route
                  path={dbPath + "/account"}
                  element={<AccountDashboard />}
                >
                  <Route path={"plan"} element={<SubscriptionPage />} />
                  <Route path={"billing"} element={<BillingPage />} />
                  <Route path={"users"} element={<UsersPage />} />
                  <Route path={"locations/:id"} element={<EditLocationPage />} />
                  <Route path={"locations"} element={<LocationsPage />} />
                  <Route path={"brands/new"} element={<ManageBrandPage />} />
                  <Route path={"brands/:id"} element={<ManageBrandPage />} />
                  <Route path={"brands"} element={<BrandsPage />} />
                  <Route path={"team"} element={<TeamPage />} />
                  {true
                    ? <Route path={"print"} element={<PrintHistoryPageNew />} />
                    : <Route path={"print"} element={<PrintHistoryPage />} />
                  }
                  <Route path={`print/order/:id`} element={<PrintOrderDetailPage />} />
                  <Route path={""} element={<UsersPage />} />
                </Route>

                <Route
                  path={"/content"}
                  element={<ContentPage />}
                >
                  <Route path={"bento"} element={<BentoContent />} />
                  <Route path={"affiliate"} element={<AffiliateContent />} />
                  <Route path={"affiliate/form"} element={<AffiliateContentForm />} />
                  <Route path={"resource-showcase"} element={<ResourceShowcasePage />} />
                  <Route path={"assets"} element={<DigitalAssetsPage path={'logos'} />} />
                  <Route path={"assets/logos"} element={<DigitalAssetsPage path={'logos'} />} />
                  <Route path={"assets/colors"} element={<DigitalAssetsPage path={'colors'} />} />
                  <Route path={"assets/imagery"} element={<DigitalAssetsPage path={'imagery'} />} />
                  <Route path={"assets/iconography"} element={<DigitalAssetsPage path={'iconography'} />} />
                  <Route path={"toast-partner-connect"} element={<ToastPartnerConnect />} />
                  <Route path={"press"} element={<PressPage />} />
                  <Route path={"press/toast-integration-announcement"} element={<ToastIntegrationAnnouncement />} />
                  <Route path={"press/display-app-announcement"} element={<DisplayAppAnnouncement />} />
                  <Route path={"shamrock"} element={<ShamrockPage />} />
                  <Route path={"shamrock-menu-services"} element={<ShamrockMenuServices />} />
                  <Route path={"shamrock-resource-center"} element={<ShamrockResourceCenter />} />
                  <Route path={"partners"} element={<PartnersPage />} />
                  <Route path={"partner/:name"} element={<PartnerPage />} />
                </Route>

                <Route
                  path={dbPath}
                  element={<Designs isSingleUserAcc={isSingleUserAcc} isUnverifiedToastUser={isUnverifiedToastUser} />}
                />
                <Route element={<Designs isSingleUserAcc={isSingleUserAcc} isUnverifiedToastUser={isUnverifiedToastUser} />} />

                <Route path={"/account/register/:inviteId"} element={<RegisterPage />} />
                <Route path={"/account/register"} element={<RegisterPage onLogin={onLogin} />} />
                <Route path={"/account/login"} element={<RegisterPage onLogin={onLogin}/>} />
                <Route path={"/account/oauth"} element={<OAuthPage />} />
                <Route path={"/account/stream/v1/authorize"} element={<StreamOAuthPage />} />
                <Route path={"/account/password"} element={<PasswordPage />} />
                <Route path={"/account/password/reset"} element={<PasswordResetPage />} />
              </Routes>
            </div>

            {/*<SurveyModal
              isVisible={isSurveyModal}
              onClose={() => setSurveyModal(false)}
            />*/}
          </main>
        </div>
      </AppContext.Provider>}
    </>
  );
}

export default App;
